import { useRef, useCallback, useEffect } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { InfoBox } from '@/components/ui/InfoBox'
import { 
  MagnifyingGlassIcon, 
  LocationMarkerIcon,
} from '@/components/ui/icons'
import type { Store } from '@/types'

// Get MapTiler style URL from AppConfig or use default
const getMapTilerStyleUrl = (): string => {
  if (typeof window !== 'undefined' && window.AppConfig?.api?.mapTilerStyleUrl) {
    return window.AppConfig.api.mapTilerStyleUrl
  }
  return 'https://api.maptiler.com/maps/c009d368-07c5-496e-8047-e69432e7af2b/style.json?key=Kaj7hj4spq6OTB0wtwjG'
}

export interface DeliveryPromptProps {
  stores: Store[]
  onSearchAddress: () => void
}

export function DeliveryPrompt({ stores, onSearchAddress }: DeliveryPromptProps) {
  return (
    <div className="flex flex-col min-h-[calc(100dvh-220px)]">
      {/* Form Content - Above the map */}
      <div className="relative z-10">
        <p className="text-sm/5 text-text-secondary mb-4">
          Enter your delivery address
        </p>

        {/* Address Search Trigger */}
        <button
          onClick={onSearchAddress}
          className="
            w-full py-3.5 px-4
            flex items-center gap-3
            text-left text-text-tertiary
            bg-surface-elevated
            border border-border-light
            rounded-xl shadow-card
            hover:shadow-card-hover hover:border-border-default
            transition-all duration-200
            active:scale-[0.99]
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary
          "
        >
          <MagnifyingGlassIcon className="size-5 text-text-tertiary" />
          <span>Search for address...</span>
        </button>

        {/* Use Current Location */}
        <button
          onClick={onSearchAddress}
          className="
            w-full mt-3 py-3.5 px-4
            flex items-center gap-3
            text-left
            bg-surface-elevated
            border border-border-light
            rounded-xl shadow-card
            hover:shadow-card-hover hover:border-border-default
            transition-all duration-200
            active:scale-[0.99]
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary
          "
        >
          <div className="size-10 flex items-center justify-center rounded-lg bg-sky-100 dark:bg-sky-900/30">
            <LocationMarkerIcon className="size-5 text-sky-600 dark:text-sky-400" />
          </div>
          <div>
            <p className="font-medium text-text-primary">
              Use current location
            </p>
            <p className="text-sm/5 text-text-secondary">
              We'll find stores that deliver to you
            </p>
          </div>
        </button>

        {/* Info Box */}
        <div className="mt-4">
          <InfoBox
            variant="warning"
            message="Delivery availability varies. Enter your address to see which stores can deliver and estimated times."
          />
        </div>
      </div>

      {/* Background Map with Store Markers - fills remaining space */}
      <div className="flex-1 mt-6 -mx-4 -mb-8 min-h-[250px]">
        <DeliveryBackgroundMap stores={stores} />
      </div>
    </div>
  )
}

interface DeliveryBackgroundMapProps {
  stores: Store[]
}

function DeliveryBackgroundMap({ stores }: DeliveryBackgroundMapProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)

  const getBounds = useCallback(() => {
    if (stores.length === 0) return null
    
    const lats = stores.map(s => s.addressLocation?.lat ?? 0)
    const lngs = stores.map(s => s.addressLocation?.long ?? 0)
    
    return new maplibregl.LngLatBounds(
      [Math.min(...lngs) - 0.02, Math.min(...lats) - 0.02],
      [Math.max(...lngs) + 0.02, Math.max(...lats) + 0.02]
    )
  }, [stores])

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: getMapTilerStyleUrl(),
      center: [144.9631, -37.8136],
      zoom: 11,
      attributionControl: false,
      interactive: false,
    })

    map.on('load', () => {
      const bounds = getBounds()
      if (bounds) {
        map.fitBounds(bounds, {
          padding: { top: 60, bottom: 60, left: 60, right: 60 },
          maxZoom: 12,
        })
      }

      stores.forEach(store => {
        const el = document.createElement('div')
        el.className = 'delivery-bg-marker'
        el.innerHTML = `
          <div class="bg-marker-pin">
            <svg viewBox="0 0 24 30" width="24" height="30">
              <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 18 12 18s12-9 12-18c0-6.627-5.373-12-12-12z" fill="var(--color-brand-primary, #B91C1C)" opacity="0.7" />
              <circle cx="12" cy="12" r="4" fill="white" opacity="0.9" />
            </svg>
          </div>
        `

        new maplibregl.Marker({
          element: el,
          anchor: 'bottom',
        })
          .setLngLat([store.addressLocation?.long ?? 0, store.addressLocation?.lat ?? 0])
          .addTo(map)
      })
    })

    mapRef.current = map

    return () => {
      map.remove()
      mapRef.current = null
    }
  }, [getBounds, stores])

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div 
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, var(--color-surface-secondary) 0%, transparent 35%, transparent 100%)',
        }}
      />
      
      <div ref={mapContainerRef} className="w-full h-full" />

      <style>{`
        .delivery-bg-marker {
          pointer-events: none;
        }
        
        .bg-marker-pin {
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
          animation: float 3s ease-in-out infinite;
        }
        
        .bg-marker-pin:nth-child(odd) {
          animation-delay: -1.5s;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
      `}</style>
    </div>
  )
}
